import { QueryClient } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { ListMetricsRequest_TargetSchema } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/aqora_service_pb';
import { LoaderFunctionArgs } from 'react-router';
import { MetricType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import { MetricDataSet } from './types';
import { METRICS_DATA_PARAM_NAME } from '~/shared/models/create-routes';
import { assertIsDefined } from '~/types/assert-type';
import { checkAuthorization } from '~/shared/auth/auth-utils';
import { ChartMetricModel } from '~/shared/models/metrics/ChartMetricModel';
import { getSafeObjectFromUrl } from '~/shared/utils/get-safe-object-from-url';
import { SimulationModel } from '~/shared/models/system/SimulationModel';
import {
  chartMetricQuery,
  systemModeMetricQuery,
} from '~/shared/api/metrics.queries';
import { getSystemSimulationQuery } from '~/shared/api/simulations.queries';
import { systemBehaviourQuery } from '~/pages/Metrics/api/behaviour.queries';
import { SystemBehaviourModel } from '~/pages/Metrics/models/SystemBehaviourModel';
import { ResourceNameIdConverter } from '~/shared/models/ResourceNameIdConverter';
import { SystemModeMetricModel } from '~/shared/models/metrics/SystemModeMetricModel';
import { createSystemBehaviourTargets } from '~/pages/Metrics/create-system-behaviour-targets';

export type MetricsInSimulationLoaderResult = {
  metrics: ChartMetricModel[];
  simulation: SimulationModel;
  historicSystemMode: SystemModeMetricModel;
  systemBehaviour?: SystemBehaviourModel;
};

export function metricsInSimulationLoader(queryClient: QueryClient) {
  return async ({
    request,
    params,
  }: LoaderFunctionArgs): Promise<MetricsInSimulationLoaderResult> => {
    await checkAuthorization(queryClient, request);

    const { simulationId, systemId } = params;

    assertIsDefined(simulationId);
    assertIsDefined(systemId);

    const simulation = await queryClient.ensureQueryData(
      getSystemSimulationQuery({ simulationId }),
    );

    const { searchParams } = new URL(request.url);

    const data = getSafeObjectFromUrl<MetricDataSet>(
      searchParams,
      METRICS_DATA_PARAM_NAME,
    );

    const promises = data
      .filter(({ metricType }) => !!metricType)
      .map(({ resourceName, metricType }) => {
        assertIsDefined(metricType);
        return queryClient.ensureQueryData(
          chartMetricQuery({
            simulation,
            target: create(ListMetricsRequest_TargetSchema, {
              resourceName,
              types: [MetricType[metricType]],
            }),
          }),
        );
      });

    const historicSystemModePromise = queryClient.ensureQueryData(
      systemModeMetricQuery({
        simulation,
        target: create(ListMetricsRequest_TargetSchema, {
          resourceName: ResourceNameIdConverter.systemIdToName(systemId),
          types: [MetricType.SYSTEM_MODE],
        }),
      }),
    );
    const systemBehaviourTargets = createSystemBehaviourTargets(data);

    const systemBehaviourPromise =
      systemBehaviourTargets.length > 0
        ? queryClient.ensureQueryData(
            systemBehaviourQuery({
              simulation,
              targets: systemBehaviourTargets,
            }),
          )
        : undefined;

    const [historicSystemMode, systemBehaviour, ...metrics] = await Promise.all(
      [historicSystemModePromise, systemBehaviourPromise, ...promises],
    );

    return { simulation, historicSystemMode, systemBehaviour, metrics };
  };
}
